import {useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';
import {CampaignProps} from '@/types/campaign';
import useNavigation from '@/hooks/use-navigation';

type UseCampaignIndexRedirectProps = {
  campaign: CampaignProps;
  campaignId: string;
  isLoading: boolean;
};

/**
 * Hook that handles redirect logic for campaign index route
 * Redirects based on proposalsCount and acceptedContractsCount
 * Prevents rendering until redirect is determined or data is loaded
 */
function useCampaignIndexRedirect({
  campaign,
  campaignId,
  isLoading,
}: UseCampaignIndexRedirectProps) {
  const navigation = useNavigation();
  const location = useLocation();
  const hasRedirected = useRef(false);

  useEffect(() => {
    // Don't redirect if still loading or already redirected
    if (isLoading || hasRedirected.current || !campaign.loaded) {
      return;
    }

    // Only redirect if we're on the base campaign route (index)
    const isIndexRoute = location.pathname === `/campaigns/${campaignId}`;
    if (!isIndexRoute) {
      return;
    }

    const {proposalsCount = 0, acceptedContractsCount = 0} = campaign;

    // Perform redirect based on the same logic as Ember route
    if (proposalsCount > 0) {
      hasRedirected.current = true;
      navigation.navigate(`/campaigns/${campaignId}/proposals`, {
        replace: true,
      });
      return;
    }

    if (acceptedContractsCount > 0) {
      hasRedirected.current = true;
      navigation.navigate(`/campaigns/${campaignId}/contracts`, {
        replace: true,
      });
      return;
    }

    hasRedirected.current = true;
    navigation.navigate(`/campaigns/${campaignId}/brief`, {replace: true});
  }, [campaign, campaignId, isLoading, location.pathname, navigation]);

  return {
    hasRedirected: hasRedirected.current,
  };
}

export default useCampaignIndexRedirect;
